import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import ApexCharts from 'apexcharts';
import {
  ApexAxisChartSeries,
  ApexOptions,
  ChartComponent,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { environment } from 'src/environments/environment';
import { PaginationMetricas } from '../../interface/tabela-obras-fase.interface';
import { MetricasService } from '../../service/metricas.service';
import {
  FiltroEstadoCidadeComponent,
  Region,
  States,
} from '../filtros-metricas/filtro-estado-cidade/filtro-estado-cidade.component';
import { FiltroPeriodoComponent } from '../filtros-metricas/filtro-periodo/filtro-periodo.component';
import { PeriodoService } from '../filtros-metricas/filtro-periodo/service/filtro-periodo.service';
import { FiltroResponsavelComponent } from '../filtros-metricas/filtro-responsavel/filtro-responsavel.component';
import { FiltroTipoObraComponent } from '../filtros-metricas/filtro-tipo-obra/filtro-tipo-obra.component';
import { ModalAguardandoDownloadComponent } from 'src/app/shared/components/modal-aguardando-download/modal-aguardando-download.component';

(window as any).ApexCharts = ApexCharts;

interface VendaRegiao {
  cidade: string;
  contratadas: number;
  vendidas: number;
}

@Component({
  selector: 'app-vendas-regiao',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    FiltroPeriodoComponent,
    FiltroResponsavelComponent,
    FiltroTipoObraComponent,
    FiltroEstadoCidadeComponent,
    ModalAguardandoDownloadComponent,
  ],
  templateUrl: './vendas-regiao.component.html',
  styleUrls: ['./vendas-regiao.component.scss'],
})
export class VendasRegiaoComponent implements OnInit {
  BASE_IMG_URL: string = environment.BASE_IMG_URL;
  viewMode: 'chart' | 'table' = 'chart';
  processandoAcao: boolean = false;
  mensagemProcessando: string = 'Aguardando download...';

  trackByFn = (index: number, item: any) => item.id;

  regiaoSelecionada: Region | null = null;
  estadoSelecionado: States | null = null;
  cidadesSelecionadas: any[] = [];
  intervaloSelecionado: Date[] | null = null;

  tiposSelecionados: any[] = [];

  arrVendas: VendaRegiao[] = [];

  config: Partial<BsDaterangepickerConfig> = {
    containerClass: 'bs-datepicker',
    rangeInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    selectFromOtherMonth: true,
    isAnimated: true,
    displayMonths: 1,
  };

  vendas: VendaRegiao[] = [];

  tableData: any[] = [];
  pagination: PaginationMetricas = {
    pageNext: 2,
    pagePrev: 0,
    recordsCount: 0,
    pageFirst: 1,
    pageLast: 1,
    pageActive: true,
    page: 1,
    pageSize: 10,
    shortcuts: 0,
    hasValue: false,
    shortcutPages: [],
    records: [],
  };

  currentPage: number = 1;
  pageNumber: number = 1;
  pageSize: number = 50;
  totalPagesValue: number = 0;

  get desabilitarExportar(): boolean {
    return this.vendas.length === 0;
  }

  idObraSelecionada: number | null = null;
  indiceObraAtual: number = 0;
  exibirFichaDaObra: boolean = false;

  chartSeries: ApexAxisChartSeries = [];
  chartOptions: Partial<ApexOptions> = {};

  @Input() selectedPeriodo: Date[] | null = null;
  @Input() selectedUsuario: string = 'Todos';
  @Input() obraIds: number[] = [];
  @Input() responsavel: any[] = [];

  @Output() rowClick = new EventEmitter<any>();
  @Output() obraSelecionada = new EventEmitter<{
    workId: number;
    obraIds: number[];
    moduleId?: number;
    workType?: string;
    workTypesPorObra?: Record<number, number>;
  }>();
  @Output() periodoChange = new EventEmitter<{
    periodBegin: string;
    periodEnd: string;
  } | null>();
  @Output() avancar = new EventEmitter<void>();
  @Output() voltarObra = new EventEmitter<void>();
  @Output() companyNameClick = new EventEmitter<{
    companyId?: number;
    companyName?: string;
    workType?: string;
  }>();
  @Output() atualizandoMetricasChange = new EventEmitter<boolean>();

  @ViewChild('chart') chart?: ChartComponent;

  private periodoService = inject(PeriodoService);

  private sincronizarPeriodoComFiltro = effect(() => {
    const periodo = this.periodoService.periodoSelecionado()?.();

    this.buscarVendasRegiao();
  });

  constructor(private _metricasService: MetricasService) {}

  ngOnInit(): void {
    this.chartOptions = {
      chart: { type: 'bar', height: 350 },
      xaxis: { categories: [''] },
      colors: ['#eb5e28'],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
      dataLabels: { enabled: false },
      tooltip: { enabled: true, y: { formatter: (val) => `${val} ` } },
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['responsavel'] && !changes['responsavel'].firstChange) {
      const responsaveis = changes['responsavel'].currentValue;

      if (
        Array.isArray(responsaveis) &&
        responsaveis.length > 0 &&
        responsaveis[0]?.userLevelId
      ) {
        const p = this.periodObjFromSelected();

        this.buscarVendasRegiao(p);
      } else {
      }
    }

    if (changes['selectedPeriodo'] && !changes['selectedPeriodo'].firstChange) {
      const periodo =
        this.selectedPeriodo?.length === 2
          ? {
              periodBegin: this.selectedPeriodo[0].toISOString().slice(0, 10),
              periodEnd: this.selectedPeriodo[1].toISOString().slice(0, 10),
            }
          : undefined;
      this.buscarVendasRegiao();
    }
  }

  buscarVendasRegiao(periodo?: {
    periodBegin: string;
    periodEnd: string;
  }): void {
    this.atualizandoMetricasChange.emit(true);
    const userLevelId =
      Array.isArray(this.responsavel) && this.responsavel.length > 0
        ? this.responsavel[0].userLevelId
        : undefined;

    if (userLevelId === undefined) {
      this.atualizandoMetricasChange.emit(false);
      return;
    }

    const companyID = localStorage.getItem('companyID') || '0';
    const cityID =
      this.cidadesSelecionadas && this.cidadesSelecionadas.length > 0
        ? this.cidadesSelecionadas.map((c) => c.cityId).join(',')
        : '0';
    let periodBegin = '0';
    let periodEnd = '0';
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      periodBegin = this.selectedPeriodo[0]?.toISOString().slice(0, 10) ?? '0';
      periodEnd = this.selectedPeriodo[1]?.toISOString().slice(0, 10) ?? '0';
    }
    let responsibleID: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleID = Number(this.selectedUsuario) || 0;
    }
    const requestBody = {
      clientID: Number(companyID),
      responsibleID: responsibleID,
      regionID: this.regiaoSelecionada?.regionId || 0,
      stateID: this.estadoSelecionado?.stateId?.toString() || '0',
      cityID:
        this.cidadesSelecionadas.length > 0
          ? this.cidadesSelecionadas.map((c) => c.cityId).join(',')
          : '0',

      moduleID:
        !this.tiposSelecionados || this.tiposSelecionados.length === 0
          ? '0'
          : this.tiposSelecionados.join(','),
      periodBegin,
      periodEnd,
    };
    this._metricasService.listWorkRegion(requestBody).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.arrVendas = response.data;
          this.vendas = response.data.map((item: any) => ({
            cidade: item.description,
            contratadas: item.quantity ?? 0,
            vendidas: 0,
          }));
        } else {
          this.vendas = [];
        }
        this.atualizarGrafico();
        this.atualizandoMetricasChange.emit(false);
      },
      error: (err) => {
        this.vendas = [];
        this.atualizarGrafico();
        this.atualizandoMetricasChange.emit(false);
        console.error('Erro ao buscar vendas por região:', err);
      },
    });
  }

  atualizarGrafico(): void {
    const seriesData = this.vendas.map((v) => ({
      x: v.cidade,
      y: v.contratadas + v.vendidas,
    }));

    this.chartSeries = [
      {
        name: 'Total de obras',
        data: seriesData.map((item) => item.y),
      },
    ];
    this.chartOptions = {
      chart: {
        type: 'bar',
        height: 350,
      },
      xaxis: {
        categories: seriesData.length ? seriesData.map((item) => item.x) : [''],
        labels: {
          rotate: -45,
        },
      },
      colors: ['#eb5e28'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '50%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val) => `${val}`,
        },
      },
    };
  }

  toggleView(mode: 'chart' | 'table') {
    this.viewMode = mode;
  }

  exportExcel() {
    this.mensagemProcessando = 'Aguardando download do Excel...';
    this.processandoAcao = true;
    const filtros = this.capturarFiltrosAtuais();
    this._metricasService.downloadExcelWorkRegion(filtros).subscribe(
      (response) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'obras-por-regiao.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.processandoAcao = false;
      },
      (error) => {
        this.processandoAcao = false;
        console.error('Erro ao exportar Excel:', error);
      }
    );
  }

  capturarFiltrosAtuais(): any {
    const filtros: any = {};

    let period: { periodBegin: string; periodEnd: string } | null = null;
    if (this.selectedPeriodo && this.selectedPeriodo.length === 2) {
      period = {
        periodBegin: this.formatDateToBackendFormat(this.selectedPeriodo[0]),
        periodEnd: this.formatDateToBackendFormat(this.selectedPeriodo[1]),
      };
    }

    if (period) {
      filtros.period = period;
    }

    let responsibleId: number = 0;
    if (this.selectedUsuario !== 'Todos') {
      responsibleId = Number(this.selectedUsuario) || 0;
    }

    if (responsibleId !== 0) {
      filtros.responsible = {
        responsible: responsibleId
      };
    }

    if (this.regiaoSelecionada) {
      filtros.region = {
        region: this.regiaoSelecionada.regionId
      };
    }

    if (this.estadoSelecionado) {
      filtros.state = {
        state: this.estadoSelecionado.stateId
      };
    }

    const cidadesIds = this.cidadesSelecionadas || [];
    if (cidadesIds.length > 0) {
      filtros.city = {
        city: cidadesIds
      };
    }

    const tiposObraIds = this.tiposSelecionados || [];
    if (tiposObraIds.length > 0) {
      filtros.type = {
        type: tiposObraIds
      };
    }

    return filtros;
  }

  private formatYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDateToBackendFormat(date: Date): string {
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  onTiposSelecionadosChange(tipos: number[]): void {
    this.tiposSelecionados = tipos;
    this.buscarVendasRegiao();
  }

  onCompanyNameClick(row: any): void {
    this.companyNameClick.emit({
      companyId: row.companyId,
      companyName: row.companyName,
      workType: row.workType,
    });
  }

  private parseYmdToLocal(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  onPeriodoSelecionadoChange(
    periodo: { periodBegin: string; periodEnd: string } | null
  ) {
    if (periodo && periodo.periodBegin && periodo.periodEnd) {
      this.selectedPeriodo = [
        this.parseYmdToLocal(periodo.periodBegin),
        this.parseYmdToLocal(periodo.periodEnd),
      ];
    } else {
      this.selectedPeriodo = null;
    }
    this.buscarVendasRegiao();
  }

  onResponsaveisRecebidos(resps) {
    if (!Array.isArray(resps)) {
      console.error('Responsáveis recebidos não são um array:', resps);
      return;
    }
    if (resps.length === 0) {
      console.warn('Array de responsáveis está vazio.');
    }

    if (resps.length > 0 && resps[0].id === undefined) {
      console.error('Responsável não possui propriedade id:', resps[0]);
    }
  }

  onRegiaoChange(regiao: Region | null) {
    this.regiaoSelecionada = regiao;
    if (!regiao) {
      this.estadoSelecionado = null;
      this.cidadesSelecionadas = [];
    }
  }

  onEstadoChange(estado: States | null) {
    this.estadoSelecionado = estado;
    if (!estado) {
      this.cidadesSelecionadas = [];
    }
  }

  onCidadesChange(cidades: any[]) {
    this.cidadesSelecionadas = Array.isArray(cidades)
      ? cidades.filter((c) => c && c.cityId !== undefined)
      : [];
    this.buscarVendasRegiao();
  }

  limparEstado() {
    this.estadoSelecionado = null;
    this.cidadesSelecionadas = [];
  }

  limparCidades() {
    this.cidadesSelecionadas = [];
  }

  onWorkNameSelected(workId: number): void {
    const obra = this.tableData.find((data) => data.workId === workId);
    const workTypesPorObra: Record<number, number> = {};
    this.tableData.forEach((data) => {
      if (data.workTypeId != null) workTypesPorObra[data.workId] = data.workTypeId;
    });
    this.obraSelecionada.emit({
      workId,
      obraIds: this.tableData.map((data) => data.workId),
      ...(obra?.workTypeId != null ? { moduleId: obra.workTypeId } : {}),
      ...(obra?.workType ? { workType: obra.workType } : {}),
      workTypesPorObra,
    });
  }

  onResponsavelIdChange(responsavelId: number): void {
    const periodo = this.periodObjFromSelected();
    this.buscarVendasRegiao(periodo);
  }

  private periodObjFromSelected():
    | { periodBegin: string; periodEnd: string }
    | undefined {
    if (this.selectedPeriodo?.length === 2) {
      return {
        periodBegin: this.formatYmd(this.selectedPeriodo[0]),
        periodEnd: this.formatYmd(this.selectedPeriodo[1]),
      };
    }
    return undefined;
  }
}
